<template>
  <div class="main-content ">
    <div class="checkout-detail-page">
      <div class="container">
        <Step />
        <MemberInfoBar />

        <DetailList title="購買明細">
          <SingleItem
            v-for="item in items"
            :key="
              `${item.type}-${
                item.product_spec ? item.product_spec.product_spec_id : ''
              }`
            "
            :item="item"
          />
        </DetailList>

        <div class="row calculation-area">
          <div class="col-4">
            <CouponBox title="優惠券折抵">
              <OneCoupon
                v-for="coupon in coupons"
                :coupon="coupon"
                :key="`coupon-id-${coupon.type}`"
                @updateQty="updateCouponQty"
              />
            </CouponBox>
          </div>
          <div class="col-4">
            <TotalDiscountBox
              title="總價折抵"
              :discountType="subtotalDiscountType"
              :amount="subtotalDiscountNumber"
              :discountPriceMax="receivableAmount"
              @updateTotalDiscount="updateTotalDiscount"
            />
          </div>
          <div class="col-4">
            <CalculateBox title="價格計算" :coupons="coupons"></CalculateBox>
          </div>
        </div>

        <PaymentMethod
          title="付款方式"
          :payment="payWay"
          @input="setPayway"
          @online-purchase="handleOnlinePurchase"
        />

        <AmountBoard
          title="應收金額"
          :receivableAmount="receivableAmount"
          :amount="amount"
          :unpaidAmount="unpaidAmount"
          @input="setAmount"
        />

        <div class="purchase-notes">
          <div class="purchase-notes__title">
            購買備註
          </div>

          <div class="purchase-notes__inpu">
            <textarea
              placeholder="請輸入備註"
              class="custom-textarea"
              v-model="remark"
            ></textarea>
          </div>
        </div>

        <button class="custom-btn" @click="confirmPayment">確認付款</button>
      </div>
    </div>

    <CollectMoney @handlePay="createOrder" :amount="amount" />
    <ReceiveComplete :orderId="orderId" @confirm="clearOrder" />
    <OrderCompleteNoInvoice
      :unpaidAmount="unpaidAmount"
      @confirm="clearOrder"
    />
    <ReceiveCompleteNoInvoice @confirm="clearOrder" />

    <!-- MessageModal -->
    <div>
      <MessageModal ref="messageModal" />
    </div>

    <!-- Confirmation Modal -->
    <ConfirmationModal
      v-if="showConfirmModal"
      :visible="showConfirmModal"
      title="確認發送購物內容"
      message="是否確認發送購物內容連結至客戶信箱"
      @confirm="handleConfirm"
      @cancel="handleCancel"
    />
  </div>
</template>

<script>
import Step from '@/components/Layouts/Step';
import CollectMoney from '@/components/Modal/CollectMoney';
import OrderCompleteNoInvoice from '@/components/Modal/OrderCompleteNoInvoice';
import ReceiveCompleteNoInvoice from '@/components/Modal/ReceiveCompleteNoInvoice';
import ReceiveComplete from '@/components/Modal/ReceiveComplete';
import SingleItem from '@/components/CheckoutDetail/SingleItem';
import OneCoupon from '@/components/CheckoutDetail/OneCoupon';
import CouponBox from '@/components/CheckoutDetail/CouponBox';
import TotalDiscountBox from '@/components/CheckoutDetail/TotalDiscountBox';
import AmountBoard from '@/components/CheckoutDetail/AmountBoard';
import PaymentMethod from '@/components/CheckoutDetail/PaymentMethod';
import CalculateBox from '@/components/CheckoutDetail/CalculateBox';
import { isMobile } from 'mobile-device-detect';
import MemberInfoBar from '@/components/MemberInfoBar';
import DetailList from '@/components/CheckoutDetail/DetailList';
import { mapGetters, mapActions, mapMutations, mapState } from 'vuex';
import { debounce, get, sumBy } from 'lodash';
import PaymentMixin from '@/mixins/payment';
import CouponsxMixin from '@/mixins/coupons';
import { createOrder } from '@/api/order';
import { createTemporaryOrder } from '@/api/checkout';
import MessageModal from '@/components/Modal/MessageModal';
import ConfirmationModal from '@/components/Modal/ConfirmationModal.vue';

export default {
  mixins: [PaymentMixin, CouponsxMixin],
  components: {
    SingleItem,
    OneCoupon,
    CouponBox,
    TotalDiscountBox,
    CalculateBox,
    Step,
    MemberInfoBar,
    DetailList,
    AmountBoard,
    PaymentMethod,
    ReceiveComplete,
    CollectMoney,
    OrderCompleteNoInvoice,
    ReceiveCompleteNoInvoice,
    MessageModal,
    ConfirmationModal
  },
  data() {
    return {
      isMobile: isMobile,
      orderId: null, //for cache
      remark: '',
      showConfirmModal: false
    };
  },
  watch: {
    amount(val) {
      // 非0元訂單不可付款0元
      if (this.receivableAmount != 0 && val == 0) {
        alert('付款金額不可為0');
        this.amount = null;
      } else if (val > this.receivableAmount) {
        this.amount = this.receivableAmount;
      } else if (val < 0) {
        this.amount = 0;
      }
    }
  },
  computed: {
    ...mapGetters('order', [
      'items',
      'order',
      'programs',
      'products',
      'subtotalDiscountNum'
    ]),
    ...mapState('order', {
      useCoupons: state => state.coupons,
      subtotalDiscountType: state => state.subtotalDiscountType,
      subtotalDiscountNumber: state => state.subtotalDiscountNumber,
      products: state => state.products,
      programs: state => state.programs,
      discount: state => state.discount,
      additionalPrice: state => state.additionalPrice,
      freebies: state => state.freebies
    }),
    ...mapGetters('member', ['memberId', 'branchId']),
    payments() {
      return [
        {
          amount: this.amount ? this.amount : 0,
          payment_method: this.payWay
        }
      ];
    },
    receivableAmount() {
      return get(this.order, 'receivable_amount');
    },
    unpaidAmount() {
      return this.receivableAmount - this.payments[0].amount;
    },
    orderPost() {
      return {
        ...this.order,
        additionalPrice: this.additionalPrice,
        freebies: this.freebies,
        discount: this.discount,
        member_id: this.memberId,
        branch_id: this.branchId,
        coupons: this.useCoupons,
        payments: this.payments,
        subtotal_discount_number: this.subtotalDiscountNum,
        subtotal_discount_type: this.subtotalDiscountType,
        description: this.remark
      };
    },
    // 商品小計
    productSubtotal() {
      return sumBy(this.products, function(product) {
        return product.subtotal;
      });
    },
    // 方案小計
    programSubtotal() {
      return sumBy(this.programs, function(program) {
        return program.subtotal;
      });
    },
    // 商品券目前使用金額
    productCouponAmount() {
      return sumBy(this.useCoupons, function(coupon) {
        if (coupon.type == 2) {
          return 100 * coupon.count;
        }
      });
    },
    // 課程100元目前使用金額
    programCouponAmount() {
      return sumBy(this.useCoupons, function(coupon) {
        if (coupon.type == 3) {
          return 100 * coupon.count;
        }
      });
    },
    // 通用券目前使用金額
    commonCouponAmount() {
      return sumBy(this.useCoupons, function(coupon) {
        if (coupon.type == 4) {
          return 100 * coupon.count;
        }
      });
    }
  },
  created() {
    if (this.isMobile) {
      alert('現在是手機開啟');
      this.$router.push({ name: '歷史購買紀錄' });
    }
    this.calcOrderPrice();
  },
  methods: {
    ...mapActions('order', ['clearOrder', 'getDiscount', 'calcOrderPrice']),
    ...mapMutations('order', [
      'setOrder',
      'setCoupons',
      'setSubtotalDiscountType',
      'setSubtotalDiscountNumber'
    ]),
    updateTotalDiscount: debounce(function({ discountType, amount }) {
      this.setSubtotalDiscountType(discountType);
      this.setSubtotalDiscountNumber(amount);

      this.calcOrderPrice();
    }, 500),
    //建立新訂單
    createOrder() {
      if (this.amount == null) {
        alert('請輸入付款金額');

        return;
      } else {
        createOrder(this.orderPost, data => {
          this.orderId = data.id;
          this.$bvModal.hide('collect-money');
          this.$store.commit('order/resetOrderDiscount');
          if (data.unpaid_amount !== 0) {
            this.$bvModal.show(`order-complete-no-invoice`);
          } else if (this.receivableAmount == 0) {
            // 0元訂單不用開立發票
            this.$bvModal.show(`receive-complete-no-invoice`);
          } else {
            this.$bvModal.show(`receive-complete`);
          }
        });
      }
    },
    confirmPayment() {
      console.log(this.orderPost);

      this.$bvModal.show('collect-money');
    },
    async handleOnlinePurchase() {
      // 檢查是否有商品或方案
      if (
        this.orderPost.products.length === 0 &&
        this.orderPost.programs.length === 0
      ) {
        this.showError('請選擇商品或方案');
        return;
      }

      // 檢查是否有超過一個方案
      if (this.orderPost.programs.length > 1) {
        this.showError('方案只能選擇一個');
        return;
      }

      // 顯示確認彈窗
      this.showConfirmModal = true;
    },
    async handleConfirm() {
      this.showConfirmModal = false;

      // 取得 user 資訊
      let userInfo = this.$store.state.user.user;

      // 防呆：POS 開單必須帶有銷售人員資訊（user_id）。
      // Vuex 未持久化，若銷售人員重新整理／重開頁面會遺失記憶體中的 user，
      // 造成 user_info 送出空值，後端建立正式訂單時 user_id 為 null 而失敗。
      // 這裡先嘗試用 token 重新取得 user（恢復），仍取不到才擋下並提示重新登入。
      // （後端最終仍以 X-USER-TOKEN 反查為準，此為前端的輔助與體驗優化）
      if (!userInfo || !userInfo.user_id) {
        try {
          userInfo = await this.$store.dispatch('user/fetchUser');
        } catch (e) {
          userInfo = null;
        }
      }
      if (!userInfo || !userInfo.user_id) {
        this.showError('登入狀態異常，請重新登入後再開單');
        return;
      }

      // 構建請求資料，添加用戶資訊
      const requestData = {
        type: 1,
        member_id: this.orderPost.member_id,
        cart_details: {
          ...this.orderPost,
          user_info: userInfo
        },
        subtotal: this.orderPost.subtotal,
        discount: this.orderPost.total - this.orderPost.receivable_amount,
        total: this.orderPost.receivable_amount,
        membership_fee: 0,
        discount_code_id: null
      };

      try {
        await new Promise((resolve, reject) => {
          createTemporaryOrder(requestData, data => resolve(data), reject);
        });

        // 成功後顯示成功訊息
        this.showSuccess('購物內容連結已成功發送至客戶信箱。');

        // 跳轉到歷史購買紀錄頁面
        this.$router.push({ name: '歷史購買紀錄' });
        this.$store.commit('order/resetOrderDiscount');
        await this.$store.dispatch('order/clearOrder');
      } catch (error) {
        this.showError('發送購物內容連結失敗，請稍後再試');
      }
    },
    handleCancel() {
      this.showConfirmModal = false;
    },
    showError(message) {
      this.$refs.messageModal.show(message, '錯誤', 'error');
    },
    showSuccess(message) {
      this.$refs.messageModal.show(message, '成功', 'success');
    }
  }
};
</script>
